import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Database, Lock, Cookie, Eye, ShieldCheck, ShieldEllipsis } from "lucide-react";
import LegalShell from "@/components/digindia/LegalShell";

const CARDS = [
  {
    id: "collection",
    icon: Eye,
    title: "Information Collection",
    accent: "#FF7A00",
    body:
      "We collect only the information needed to deliver your project — your name, business name, GSTIN, contact details, payment receipts and the assets you upload (logos, photos, copy). We never sell or rent your data.",
  },
  {
    id: "usage",
    icon: Database,
    title: "Data Usage",
    accent: "#FF9933",
    body:
      "Data is used strictly to design, build and ship your website, app or AI chat bot, send service updates and meet our legal/tax obligations under Indian law. We never use your business data to train external AI models.",
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "Data Security",
    accent: "#00C853",
    body:
      "All data in transit is encrypted via TLS 1.2+. Production credentials live in encrypted secret stores with role-based access. Backups are taken daily and retained for 30 days. We follow OWASP Top-10 hygiene on every project.",
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "Cookies Policy",
    accent: "#FF512F",
    body:
      "We use first-party cookies for theme preference and authentication, and privacy-friendly analytics (aggregated, no fingerprinting). No third-party advertising cookies are placed on our marketing site.",
  },
  {
    id: "rights",
    icon: Lock,
    title: "Your Rights",
    accent: "#138808",
    body:
      "You can access, correct, port or request deletion of your personal data anytime by emailing privacy@digindia.tech. We respond within 30 days as required under the DPDP Act, 2023.",
  },
];

const ParticleBackdrop = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = (canvas.width = canvas.offsetWidth);
    let h = (canvas.height = canvas.offsetHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const NUM = 38;
    const particles = Array.from({ length: NUM }).map(() => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: 1 + Math.random() * 1.6,
    }));

    const onResize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", onResize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      // Lines
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 130 * 130) {
            const a = 1 - Math.sqrt(d2) / 130;
            ctx.strokeStyle = `rgba(255,122,0,${a * 0.18})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }
      // Dots
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        ctx.fillStyle = "rgba(0,200,83,0.55)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-50 dark:opacity-70 pointer-events-none"
      data-testid="privacy-particles"
    />
  );
};

const Card = ({ c, i }) => {
  const Icon = c.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: i * 0.06 }}
      className="group relative rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-7 overflow-hidden transition-all duration-500 hover:-translate-y-1"
      data-testid={`privacy-card-${c.id}`}
    >
      <div
        className="absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl opacity-30 group-hover:opacity-70 transition-opacity duration-500"
        style={{ background: c.accent }}
      />
      <div className="relative space-y-4">
        <div
          className="h-12 w-12 rounded-xl grid place-items-center"
          style={{
            background: `${c.accent}1f`,
            border: `1px solid ${c.accent}40`,
          }}
        >
          <Icon className="h-5 w-5" style={{ color: c.accent }} />
        </div>
        <h3 className="font-heading font-bold text-xl tracking-tight">
          {c.title}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
          {c.body}
        </p>
      </div>
    </motion.div>
  );
};

export default function Privacy() {
  return (
    <LegalShell
      kicker="Legal · Privacy"
      title="Privacy Policy"
      accent="#00C853"
      icon={ShieldEllipsis}
      intro="Your data, your rules. A plain-English explanation of what we collect, why we collect it, and how we keep it safe — DPDP Act ready."
      testid="privacy-page"
    >
      <div className="relative rounded-3xl border border-border overflow-hidden mb-10 md:mb-14">
        <div className="relative h-44 md:h-56">
          <ParticleBackdrop />
          <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
          <div className="relative h-full px-6 md:px-10 flex flex-col justify-end pb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00C853]">
              Live data flow
            </div>
            <div className="font-heading font-bold text-xl md:text-2xl tracking-tight max-w-md">
              Encrypted in transit · Compartmentalised at rest
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {CARDS.map((c, i) => (
          <Card key={c.id} c={c} i={i} />
        ))}
      </div>

      <div className="mt-14 rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-8 text-sm text-muted-foreground">
        Privacy questions or data requests? Reach our DPO at{" "}
        <a className="text-[#00C853] font-semibold" href="mailto:privacy@digindia.tech">
          privacy@digindia.tech
        </a>
        . We respond within one business day.
      </div>
    </LegalShell>
  );
}
